import {
  WatchfaceDeviceInfo,
  WatchfaceDeviceType,
  useWatchfaceDevices
} from '@/hooks';
import { WatchfaceService } from '@/services/watchface';
import { Description } from '@/types/watchface';
import { EventType, emitter } from '@/utils/emitter.utils';
import { cloneDeep } from 'lodash-es';
import { defineStore } from 'pinia';

const _findDevice = (deviceType: string) => {
  const watchfaceDevices = useWatchfaceDevices().wholeDevices.value;
  return watchfaceDevices.find((item) => item.deviceType == deviceType);
};

export const useWatchfaceStore = defineStore('watchface-store', {
  state: () => ({
    path: '',
    description: {} as Description,
    // 表盘功耗信息
    powerConsumptionLevel: undefined as number | undefined,
    slotFollowing: false
  }),
  getters: {
    name(state) {
      return state.description?.name;
    },
    _id(state) {
      return state.description?._id;
    },
    colorMode(state) {
      const { watchfaceType = 'normal' } = state.description ?? {};
      const types: Description['watchfaceType'][] = [
        'photoAlbum',
        'portrait',
        'textWatchface',
        'videoWatchface'
      ];
      return types.includes(watchfaceType) ? 'recolor' : 'colorGroup';
    },
    /**
     * 返回表盘大小信息
     * @param state
     */
    size(state) {
      const size =
        _findDevice(state.description.deviceType ?? '')?.size ??
        state.description.size;
      const [width, height] = size?.split('x') ?? [];
      return {
        width: Number(width ?? 0),
        height: Number(height ?? 0)
      };
    },
    deviceType(state) {
      return state.description.deviceType as WatchfaceDeviceType;
    },
    /**
     * 返回预览图大小信息
     * @param state
     */
    previewSize(state) {
      return (
        _findDevice(state.description.deviceType ?? '')?.preview ?? {
          width: 0,
          height: 0
        }
      );
    },
    previewEnhancedSize(state) {
      return _findDevice(state.description.deviceType ?? '')?.previewEnhanced;
    },
    /**
     * 返回表盘类型：普通表盘、人像表盘、相册表盘
     * @param state
     */
    watchfaceType(state) {
      return state.description.watchfaceType ?? 'normal';
    },

    /**
     * 表盘设备信息
     * @param state
     * @returns
     */
    watchfaceDevice(state): WatchfaceDeviceInfo | undefined {
      return useWatchfaceDevices().wholeDevices.value.find(
        (item) => item.deviceType == state.description.deviceType
      );
    },

    /**
     * 返回表盘容器是否可跟随
     * @param state
     */
    isSlotFollowing(state) {
      return state.slotFollowing ?? false;
    }
  },
  actions: {
    async sync(desc?: Description) {
      if (desc) {
        this.$state.description = desc;
      } else {
        const res =
          await new WatchfaceService().description.getDescriptionData();
        const description = cloneDeep(res);
        const size1 = _findDevice(description?.deviceType ?? '')?.size;
        if (size1 != description?.size && size1 && description) {
          description.size = size1;
        }
        this.$state.description = (description ?? {}) as Description;
      }
    },
    async syncSlotFollowing(isSlotFollowing?: boolean) {
      if (isSlotFollowing) {
        this.$state.slotFollowing = isSlotFollowing;
      } else {
        this.$state.slotFollowing = false;
      }
    },
    async update(data: Partial<Description>) {
      const desc = this.description as Description;
      Object.assign(desc, data);
      this.description = desc;
      emitter.emit(EventType['watchface:description-update']);
    }
  }
});
